import { PropsWithChildren, useEffect, useState } from "react";

import {
  AppShell,
  Avatar,
  Flex,
  Group,
  Image,
  Text,
  Stack,
  UnstyledButton,
  Button,
  Menu,
  useMantineTheme,
  useMantineColorScheme,
  rem,
  NavLink,
  Drawer,
  ScrollArea,
  Divider,
  Modal,
  Burger,
  Box,
  Select,
  Tabs,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { Form, useRouteLoaderData, useFetcher } from "@remix-run/react";
import {
  IconBook,
  IconChevronDown,
  IconClipboardList,
  IconLogout,
  IconMessages,
  IconMoon,
  // IconQuestionMark,
  IconSun,
  IconTarget,
  IconUserCircle,
} from "@tabler/icons-react";
import _ from "lodash";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Notification as NotificationComponent } from "../Notification";
import { getToolNameForUrl } from "../Tour";
import { AboutActionHubSteps } from "../Tour/about-actionhub-tour";
import { DashboardSteps } from "../Tour/dashboard-tour";
import { ResourcesHomeSteps } from "../Tour/resources-home-tour";
import { useTour } from "../Tour/tour";

import { ActionHubNavItem, NAV_SECTIONS } from "./nav-items";
import { NavSectionHeader } from "./NavSectionHeader";
import classes from "./styles.module.css";

import SimsiLogo from "~/assets/Simsi_Logo_White.svg";
import { Hub, User } from "~/db/models";
import { useIsMobile } from "~/hooks/useIsMobile";
import { useStore } from "~/hooks/zustend";
import type { NotificationListItem } from "~/types/notification";
import {
  CookieName,
  NavTab,
  getNavActiveTabFromCookies,
  getNavCollapsedSectionsFromCookies,
  setCookie,
  setNavCollapsedSectionsCookie,
} from "~/utils/cookie";
import { getRouteWithCurrentHubId } from "~/utils/hubid";

// Default landing pages for each navigation tab
const DEFAULT_TAB_PAGES: Record<NavTab, string> = {
  tracking: "tasks",
  training: "learning-center/library",
  comms: "collaborate/discussions",
  cardiff: "cardiff",
};

function NavButton({
  tourTarget,
  title,
  link,
  icon: Icon,
  selected,
  onClick,
  disabled = false,
  isMobile = false,
}: {
  tourTarget: string;
  title: string;
  link: string;
  icon: React.FC<any>;
  selected?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  isMobile?: boolean;
}) {
  return (
    <NavLink
      label={title}
      leftSection={<Icon size={20} />}
      active={selected}
      component={Link}
      to={`/${link}`}
      onClick={onClick}
      disabled={disabled}
      className={isMobile ? classes.mobileNavLink : classes.navLink}
      data-tour-target={tourTarget}
    />
  );
}

function NoTourModal({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  return (
    <Modal title="No Tour Yet" opened={opened} onClose={onClose}>
      No tour available for this tool
    </Modal>
  );
}

// Pure Shell component that accepts all data as props (testable with dependency injection)
export function ShellPresentation({
  children,
  notifications,
  requestHeaders,
  currentHubId,
  hasRtmdxUsers,
  user,
  hubs,
  isHubAdmin,
  canEditCourses,
  canManageCrimeData,
  onLogout,
  onUpdateUserHub,
  initialHubName,
}: PropsWithChildren & {
  notifications: NotificationListItem[];
  requestHeaders: { [key: string]: string };
  currentHubId: string;
  hasRtmdxUsers: boolean;
  user?: User & { username: string; profile: string };
  hubs?: Hub[];
  isHubAdmin: boolean;
  canEditCourses: boolean;
  canManageCrimeData: boolean;
  onLogout?: () => void;
  onUpdateUserHub?: (hubId: string, userEmail: string) => void;
  initialHubName?: string;
}) {
  const theme = useMantineTheme();
  const { setCurrentHubId } = useStore();
  const [userMenuOpened, { toggle: toggleUserMenu }] = useDisclosure();
  const [, { toggle: toggleHubsMenu }] = useDisclosure();
  const [mobileNavOpened, { toggle: toggleMobileNav }] = useDisclosure(false);
  const [allHubsModalOpened, { open: openAllHubsModal, close: closeAllHubsModal }] =
    useDisclosure(false);
  const hubPickerFetcher = useFetcher<{ hubs: Hub[] }>();
  const [selectedAllHubId, setSelectedAllHubId] = useState<string | null>(null);
  const isMobile = useIsMobile(requestHeaders);

  // Track if component has mounted to prevent mobile drawer SSR flash
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const isActionHubLandingPage = pathname === "/action-hub";
  const selectedHub = hubs?.find((hub: Hub) => hub.id == currentHubId);
  const selectedHubFromAll = hubPickerFetcher.data?.hubs?.find(
    (hub) => hub.id === currentHubId,
  );
  const activeHub = selectedHub || selectedHubFromAll;
  const [currentHubName, setCurrentHubName] = useState(
    activeHub?.name || initialHubName || "Loading hub...",
  );
  const isGlobalAdmin =
    user?.roles?.includes("admin") || user?.roles?.includes("superuser");
  const hasHubs = user && hubs?.length > 0;
  const canShowNav = user && (hasHubs || isGlobalAdmin);
  const learningCenterEnabled = activeHub?.learning_center_enabled ?? false;
  const crimeDataEnabled = activeHub?.crime_data_enabled ?? true;
  const cardiffEnabled = activeHub?.cardiff_enabled ?? false;
  const trackingEnabled = activeHub?.tracking_enabled ?? true;
  const commsEnabled = activeHub?.comms_enabled ?? true;

  // Derive active tab from URL pathname (single source of truth)
  const getTabFromPathname = (path: string): NavTab | null => {
    // Remove hubId prefix for cleaner matching
    const pathWithoutHub = path.replace(`/${currentHubId}/`, "/").replace(`/${currentHubId}`, "/");

    // Training tab routes
    if (pathWithoutHub.startsWith("/learning-center")) return "training";

    // Cardiff tab routes
    if (pathWithoutHub.startsWith("/cardiff")) return "cardiff";

    // Comms tab routes
    if (pathWithoutHub.startsWith("/collaborate") || pathWithoutHub.startsWith("/sharing")) return "comms";

    // Tracking tab routes
    if (
      pathWithoutHub.startsWith("/tasks") ||
      pathWithoutHub.startsWith("/action-plans") ||
      pathWithoutHub.startsWith("/locator") ||
      pathWithoutHub.startsWith("/crime-data") ||
      pathWithoutHub.startsWith("/resource-inventory") ||
      pathWithoutHub.startsWith("/rtmdx")
    ) {
      return "tracking";
    }

    // Home routes to the active tab (null = use cookie)
    if (pathWithoutHub.startsWith("/home")) return null;

    // Dashboard lives under Tracking
    if (pathWithoutHub.startsWith("/dashboard")) return "tracking";

    // Ambiguous routes (teamwork, etc.) - return null to use cookie fallback
    return null;
  };

  // Nav tab and collapsed sections state (SSR-compatible via cookies)
  const cookieHeader = requestHeaders?.cookie || requestHeaders?.Cookie;
  const initialCookieTab = getNavActiveTabFromCookies(cookieHeader);
  const initialCollapsedSections = getNavCollapsedSectionsFromCookies(cookieHeader);
  const [clientCookieTab, setClientCookieTab] = useState<NavTab>(initialCookieTab);

  // Ordered list of tabs that are actually enabled for this hub
  const availableTabs: NavTab[] = [
    ...(trackingEnabled ? ["tracking" as NavTab] : []),
    ...(commsEnabled ? ["comms" as NavTab] : []),
    ...(learningCenterEnabled ? ["training" as NavTab] : []),
    ...(cardiffEnabled ? ["cardiff" as NavTab] : []),
  ];
  const fallbackTab: NavTab = availableTabs[0] ?? "tracking";

  // Derive tab from URL; null means ambiguous (home, teamwork, etc.) — use cookie
  const derivedTab = getTabFromPathname(pathname);
  // Cookie tab is valid only if that tab is still enabled
  const effectiveCookieTab = availableTabs.includes(clientCookieTab) ? clientCookieTab : fallbackTab;
  // Final active tab: URL wins if it maps to an available tab, otherwise use cookie fallback
  const activeTab = availableTabs.includes(derivedTab ?? effectiveCookieTab)
    ? (derivedTab ?? effectiveCookieTab)
    : fallbackTab;

  const [collapsedSections, setCollapsedSections] = useState<string[]>(initialCollapsedSections);

  // Update cookie when tab changes based on URL
  useEffect(() => {
    if (derivedTab) {
      setCookie(CookieName.NAV_ACTIVE_TAB, derivedTab);
      setClientCookieTab(derivedTab);
    }
  }, [derivedTab]);

  const handleTabChange = (tab: string | null, closeMobileNav = false) => {
    if (!tab || (tab !== "tracking" && tab !== "training" && tab !== "comms" && tab !== "cardiff")) return;
    setCookie(CookieName.NAV_ACTIVE_TAB, tab);
    setClientCookieTab(tab);
    if (closeMobileNav) toggleMobileNav();
    navigate(`/${currentHubId}/${DEFAULT_TAB_PAGES[tab]}`, { replace: false });
  };

  const toggleSectionCollapse = (sectionTitle: string) => {
    setCollapsedSections((prev) => {
      const isCurrentlyCollapsed = prev.includes(sectionTitle);
      const newCollapsed = isCurrentlyCollapsed
        ? prev.filter((s) => s !== sectionTitle)
        : [...prev, sectionTitle];
      setNavCollapsedSectionsCookie(newCollapsed);
      return newCollapsed;
    });
  };

  const updateUserCurrentHubId = (currentHubId: string, user: any) => {
    if (onUpdateUserHub && user?.email) {
      onUpdateUserHub(currentHubId, user.email);
    }
  };

  const setCurrentHub = async (hubId: string) => {
    setCookie(CookieName.CURRENT_HUB_ID, hubId);
    setCurrentHubId(hubId);
    updateUserCurrentHubId(hubId, user);
    const nameFromUserHubs = hubs?.find((h) => h.id === hubId)?.name;
    const nameFromAllHubs = hubPickerFetcher.data?.hubs?.find(
      (h) => h.id === hubId,
    )?.name;
    const fallbackName = currentHubName || "Loading hub...";
    setCurrentHubName(nameFromUserHubs || nameFromAllHubs || fallbackName);
    return navigate(pathname?.replace(currentHubId, hubId), {
      replace: true,
    });
  };

  const ensureAllHubsLoaded = () => {
    if (isGlobalAdmin && hubPickerFetcher.state === "idle" && !hubPickerFetcher.data) {
      hubPickerFetcher.load("/api/admin/hubs");
    }
  };

  useEffect(() => {
    if (
      isGlobalAdmin &&
      !selectedHub &&
      hubPickerFetcher.state === "idle" &&
      !hubPickerFetcher.data
    ) {
      hubPickerFetcher.load("/api/admin/hubs");
    }
  }, [isGlobalAdmin, selectedHub, hubPickerFetcher]);

  useEffect(() => {
    if (
      allHubsModalOpened &&
      hubPickerFetcher.data?.hubs &&
      !selectedAllHubId
    ) {
      setSelectedAllHubId(currentHubId || hubPickerFetcher.data.hubs[0]?.id || null);
    }
  }, [allHubsModalOpened, hubPickerFetcher.data, currentHubId, selectedAllHubId]);

  useEffect(() => {
    const nameFromUserHubs = hubs?.find((h) => h.id === currentHubId)?.name;
    const nameFromAllHubs = hubPickerFetcher.data?.hubs?.find(
      (h) => h.id === currentHubId,
    )?.name;
    const resolvedName = nameFromUserHubs || nameFromAllHubs;

    if (resolvedName && resolvedName !== currentHubName) {
      setCurrentHubName(resolvedName);
    }
  }, [currentHubId, currentHubName, hubs, hubPickerFetcher.data]);

  const mobileNavStyle = {
    backgroundColor: theme.colors.brandPrimaryDark[8],
  };

  const mobileUserContent = user && (
    <>
      <Flex align="center" mb="md" p="xs">
        <Avatar
          src={user.profile?.presignedUrl}
          alt={user.username}
          radius="xl"
          size={20}
          mr="sm"
          color={isMobile ? "gray.0" : undefined}
        />
        <Text fw={500} size="sm" className={classes.mobileNavText}>
          {user.username}
        </Text>
      </Flex>
      <NavLink
        label={colorScheme === "dark" ? "Light Mode" : "Dark Mode"}
        leftSection={
          colorScheme === "dark" ? (
            <IconSun style={{ width: rem(20), height: rem(20) }} stroke={1.5} />
          ) : (
            <IconMoon
              style={{ width: rem(20), height: rem(20) }}
              stroke={1.5}
            />
          )
        }
        onClick={() => toggleColorScheme()}
        className={classes.mobileNavLink}
      />
      <NavLink
        label="Logout"
        leftSection={
          <IconLogout
            style={{ width: rem(20), height: rem(20) }}
            stroke={1.5}
          />
        }
        onClick={() => onLogout?.()}
        className={classes.mobileNavLink}
      />
      <Divider my="md" color={theme.colors.gray[6]} />
    </>
  );

  const isNavItemSelected = (item: ActionHubNavItem, pathname: string) => {
    if (_.isString(item.highlightSegment)) {
      return item.link === `${currentHubId}/`
        ? pathname === `/${currentHubId}/`
        : pathname.startsWith(`/${currentHubId}/${item.highlightSegment}`);
    } else if (_.isRegExp(item.highlightSegment)) {
      return item.highlightSegment.test(pathname);
    }

    return false;
  };

  // Filter sections by active tab (show "always" sections + sections matching active tab).
  // Cardiff omits Overview, but keeps Administration so Cardiff-only hubs can still reach Manage Users.
  const filteredSections = NAV_SECTIONS(
    currentHubId,
    hasRtmdxUsers,
    canEditCourses,
    learningCenterEnabled,
    canManageCrimeData,
    crimeDataEnabled,
    cardiffEnabled,
    isHubAdmin,
    trackingEnabled,
    commsEnabled,
  )?.filter((section) =>
    activeTab === "cardiff"
      ? section.category === "cardiff" || section.sectionTitle === "Administration"
      : section.category === "always" || section.category === activeTab,
  );

  const mobileTabsContent = user && (
    <Tabs
      value={activeTab}
      onChange={(tab) => handleTabChange(tab, true)}
      mb="md"
      classNames={{
        list: classes.mobileTabsList,
        tab: classes.mobileTab,
      }}
    >
      <Tabs.List grow>
        {trackingEnabled && (
          <Tabs.Tab
            value="tracking"
            leftSection={<IconTarget size={16} />}
          >
            Tracking
          </Tabs.Tab>
        )}
        {learningCenterEnabled && (
          <Tabs.Tab
            value="training"
            leftSection={<IconBook size={16} />}
          >
            Training
          </Tabs.Tab>
        )}
        {cardiffEnabled && (
          <Tabs.Tab
            value="cardiff"
            leftSection={<IconClipboardList size={16} />}
          >
            Cardiff
          </Tabs.Tab>
        )}
        {commsEnabled && (
          <Tabs.Tab
            value="comms"
            leftSection={<IconMessages size={16} />}
          >
            Comms
          </Tabs.Tab>
        )}
      </Tabs.List>
    </Tabs>
  );

  const navContent = (
    <ScrollArea h="100%" className={isMobile ? undefined : classes.navScrollArea}>
      <Stack gap={0} className={classes.navContent}>
        {isMobile && mobileUserContent}
        {isMobile && mobileTabsContent}
        {currentHubId &&
          filteredSections?.map((section) => (
            <Box key={section.sectionTitle} className={classes.navSection}>
              {/* Overview section (always category) - no header, just items */}
              {section.category === "always" ? (
                <Stack gap="2px">
                  {section.items.map((item) => (
                    <NavButton
                      tourTarget={item.tourTarget || ""}
                      key={item.title}
                      title={item.title}
                      link={item.link}
                      icon={item.icon}
                      selected={isNavItemSelected(item, pathname)}
                      onClick={isMobile ? toggleMobileNav : undefined}
                      disabled={!canShowNav && item.checkPermission}
                      isMobile={isMobile}
                    />
                  ))}
                </Stack>
              ) : (
                /* Other sections - collapsible with header */
                <NavSectionHeader
                  title={section.sectionTitle}
                  isCollapsed={collapsedSections.includes(section.sectionTitle)}
                  onToggle={() => toggleSectionCollapse(section.sectionTitle)}
                  isMobile={isMobile}
                  isActive={section.items.some((item) => isNavItemSelected(item, pathname))}
                  badge={section.badge}
                >
                  <Stack gap="2px">
                    {section.items.map((item) => (
                      <NavButton
                        tourTarget={item.tourTarget || ""}
                        key={item.title}
                        title={item.title}
                        link={item.link}
                        icon={item.icon}
                        selected={isNavItemSelected(item, pathname)}
                        onClick={isMobile ? toggleMobileNav : undefined}
                        disabled={!canShowNav && item.checkPermission}
                        isMobile={isMobile}
                      />
                    ))}
                  </Stack>
                </NavSectionHeader>
              )}
            </Box>
          ))}
      </Stack>
    </ScrollArea>
  );

  // Tour content
  const { tour: aboutActionHubTour /*, restart: restartAboutActionHubTour */ } =
    useTour(AboutActionHubSteps(currentHubId));

  const { tour: dashboardTour, restart: restartDashboardTour } =
    useTour(DashboardSteps);

  const { tour: resourcesHomeTour, restart: restartResourcesHomeTour } =
    useTour(ResourcesHomeSteps);

  // Tour state
  const [noTourModalOpen, setNoTourModalOpen] = useState(false);
  const currentTool = getToolNameForUrl(pathname);

  let currentToolTour;
  if (currentTool) {
    const toolTours: { [key: string]: { tour: any; restart: () => void } } = {
      home: { tour: dashboardTour, restart: restartDashboardTour },
      resourcesHome: {
        tour: resourcesHomeTour,
        restart: restartResourcesHomeTour,
      },
    };

    currentToolTour = toolTours[currentTool];
  } else {
    currentToolTour = {
      tour: null,
      restart: () => {
        setNoTourModalOpen(true);
      },
    };
  }

  // Desktop: render AppShell.Navbar
  // Mobile: only render Drawer after mount to prevent SSR portal issues
  const mobileAwareNavContent = isMobile ? (
    // Mobile - don't render Drawer during SSR (portals cause flash)
    hasMounted ? (
      <Drawer
        opened={mobileNavOpened}
        onClose={toggleMobileNav}
        size="100%"
        padding="md"
        title="Navigation"
        zIndex={1000000}
        classNames={{
          content: classes.mobileNavContent,
          header: classes.mobileNavContent,
          title: classes.mobileNavText,
          close: classes.mobileNavText,
        }}
        styles={{
          content: mobileNavStyle,
          header: mobileNavStyle,
        }}
      >
        {navContent}
      </Drawer>
    ) : null
  ) : (
    // Desktop - render normally
    <AppShell.Navbar className={classes.navbar}>
      {navContent}
    </AppShell.Navbar>
  );

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={
        isMobile
          ? undefined
          : { width: canShowNav ? 200 : 0, breakpoint: "sm" }
      }
      padding={isActionHubLandingPage ? 0 : "md"}
      styles={{
        main: {
          paddingLeft: isMobile ? 0 : undefined,
          paddingRight: isMobile ? 0 : undefined,
        },
      }}
    >
      {aboutActionHubTour}
      {currentToolTour?.tour}
      <NoTourModal
        opened={noTourModalOpen}
        onClose={() => setNoTourModalOpen(false)}
      />
      <AppShell.Header className={classes.header}>
        <Flex h="100%" justify="space-between" align="center" px="md">
          <Group gap={0}>
            {!isMobile && (
              <Link
                to={getRouteWithCurrentHubId({
                  currentHubId,
                  route: "home",
                })}
                aria-label="Go to home"
              >
                <Image src={SimsiLogo} h={32} w={111} ml="md" alt="Simsi" />
              </Link>
            )}
            {!isMobile && user && (
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                ml="lg"
                classNames={{
                  list: classes.topTabsList,
                  tab: classes.topTab,
                }}
              >
                <Tabs.List>
                  {trackingEnabled && (
                    <Tabs.Tab
                      value="tracking"
                      leftSection={<IconTarget size={16} />}
                    >
                      Tracking
                    </Tabs.Tab>
                  )}
                  {learningCenterEnabled && (
                    <Tabs.Tab
                      value="training"
                      leftSection={<IconBook size={16} />}
                    >
                      Training
                    </Tabs.Tab>
                  )}
                  {cardiffEnabled && (
                    <Tabs.Tab
                      value="cardiff"
                      leftSection={<IconClipboardList size={16} />}
                    >
                      Cardiff
                    </Tabs.Tab>
                  )}
                  {commsEnabled && (
                    <Tabs.Tab
                      value="comms"
                      leftSection={<IconMessages size={16} />}
                    >
                      Comms
                    </Tabs.Tab>
                  )}
                </Tabs.List>
              </Tabs>
            )}
            {isMobile && (
              <Burger
                opened={mobileNavOpened}
                onClick={toggleMobileNav}
                size="sm"
                color={theme.white}
              />
            )}
            {/* {isMobile ? (
              <>
                <Burger
                  opened={mobileNavOpened}
                  onClick={toggleMobileNav}
                  size="sm"
                  color={theme.white}
                />
                { <Menu
                  width={260}
                  // position="bottom-end"
                  transitionProps={{ transition: "pop-top-right" }}
                  withinPortal
                  ml="md"
                >
                  <Menu.Target>
                    <ActionIcon variant="light" color="gray.0">
                      <IconQuestionMark size={20} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item onClick={restartAboutActionHubTour}>
                      About ActionHub
                    </Menu.Item>
                    <Menu.Item onClick={currentToolTour?.restart}>
                      Help with Current Tool
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu> }
              </>
            ) : (
              <>
                { <Button
                  variant="subtle"
                  color="gray.0"
                  ml="md"
                  onClick={restartAboutActionHubTour}
                >
                  About ActionHub
                </Button>
                <Button
                  variant="subtle"
                  color="gray.0"
                  onClick={currentToolTour?.restart}
                >
                  Help with Current Tool
                </Button> }
              </> */}
          </Group>
          <Flex align="center" gap="sm">
            {user && (
              <NotificationComponent
                notifications={notifications}
                hubId={currentHubId}
              />
            )}
            {(hubs?.length || isGlobalAdmin) ? (
              <Menu
                width={260}
                position="bottom-end"
                transitionProps={{ transition: "pop-top-right" }}
                onClose={toggleHubsMenu}
                onOpen={toggleHubsMenu}
                withinPortal
              >
                <Menu.Target>
                  <Button variant="subtle" color="gray.0">
                    Hub: {currentHubName}
                  </Button>
                </Menu.Target>
                <Menu.Dropdown style={{ overflow: "auto", maxHeight: "300px" }}>
                  {isGlobalAdmin && (
                    <Menu.Label>Global hub access</Menu.Label>
                  )}
                  {isGlobalAdmin && (
                    <Menu.Item
                      fw={600}
                      color="blue"
                      onClick={() => {
                        ensureAllHubsLoaded();
                        setSelectedAllHubId(currentHubId);
                        openAllHubsModal();
                      }}
                    >
                      Pick from all hubs
                    </Menu.Item>
                  )}
                  {isGlobalAdmin && hubs?.length ? <Menu.Divider /> : null}
                  {hubs?.length ? (
                    hubs.map((hub: Hub) => (
                      <Menu.Item
                        key={`item_${hub.id}`}
                        onClick={() => setCurrentHub(hub.id)}
                      >
                        {hub.name}
                      </Menu.Item>
                    ))
                  ) : (
                    <Menu.Item disabled>No assigned hubs</Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            ) : null}
            {user ? (
              <Menu
                width={260}
                position="bottom-end"
                transitionProps={{ transition: "pop-top-right" }}
                onClose={toggleUserMenu}
                onOpen={toggleUserMenu}
                withinPortal
              >
                <Menu.Target>
                  <UnstyledButton data-active={userMenuOpened || undefined}>
                    <Group gap={7}>
                      <Avatar
                        src={user.profile?.presignedUrl}
                        alt={user.username}
                        radius="xl"
                        size={30}
                        color={"gray.0"}
                      />
                      {!isMobile && (
                        <>
                          <Text fw={500} size="sm" lh={1} mr={3} c="gray.0">
                            {user.username}
                          </Text>
                          <IconChevronDown
                            style={{ width: rem(12), height: rem(12) }}
                            stroke={1.5}
                            color={theme.colors.gray[0]}
                          />
                        </>
                      )}
                    </Group>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>Settings</Menu.Label>
                  <Menu.Item
                    onClick={() => toggleColorScheme()}
                    leftSection={
                      colorScheme === "dark" ? (
                        <IconSun
                          style={{ width: rem(16), height: rem(16) }}
                          stroke={1.5}
                        />
                      ) : (
                        <IconMoon
                          style={{ width: rem(16), height: rem(16) }}
                          stroke={1.5}
                        />
                      )
                    }
                  >
                    {colorScheme === "dark" ? "Light Mode" : "Dark Mode"}
                  </Menu.Item>
                  <Menu.Item
                    component={Link}
                    to={getRouteWithCurrentHubId({
                      currentHubId,
                      route: "profile/edit",
                    })}
                    leftSection={
                      <IconUserCircle
                        style={{ width: rem(16), height: rem(16) }}
                        stroke={1.5}
                      />
                    }
                  >
                    Edit Profile
                  </Menu.Item>
                  {isHubAdmin && (
                    <Menu.Item
                      component={Link}
                      to={getRouteWithCurrentHubId({
                        currentHubId,
                        route: "settings",
                      })}
                      leftSection={
                        <IconUserCircle
                          style={{ width: rem(16), height: rem(16) }}
                          stroke={1.5}
                        />
                      }
                    >
                      Hub Settings
                    </Menu.Item>
                  )}
                  <Menu.Item
                    onClick={() => onLogout?.()}
                    leftSection={
                      <IconLogout
                        style={{ width: rem(16), height: rem(16) }}
                        stroke={1.5}
                      />
                    }
                  >
                    Logout
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            ) : (
              <Form method="post" action="/auth/auth0">
                <Button variant="subtle" color="gray.0" type="submit">
                  Login
                </Button>
              </Form>
            )}
          </Flex>
        </Flex>
      </AppShell.Header>
      <Modal
        opened={allHubsModalOpened}
        onClose={() => {
          closeAllHubsModal();
          setSelectedAllHubId(null);
        }}
        title="Switch hub"
        centered
      >
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Global admin access to all hubs
          </Text>
          <Select
            data={
              hubPickerFetcher.data?.hubs?.map((hub) => ({
                value: hub.id,
                label: hub.name,
              })) || []
            }
            placeholder="Select a hub"
            value={selectedAllHubId}
            onChange={setSelectedAllHubId}
            searchable
            nothingFoundMessage={
              hubPickerFetcher.state === "loading"
                ? "Loading hubs..."
                : "No hubs found"
            }
            disabled={hubPickerFetcher.state === "loading"}
            withinPortal
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={closeAllHubsModal}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (selectedAllHubId) {
                  setCurrentHub(selectedAllHubId);
                  closeAllHubsModal();
                  setSelectedAllHubId(null);
                }
              }}
              disabled={!selectedAllHubId}
            >
              Switch hub
            </Button>
          </Group>
        </Stack>
      </Modal>
      {user && (hasHubs || isGlobalAdmin) && mobileAwareNavContent}
      <AppShell.Main>{children}</AppShell.Main>
    </AppShell>
  );
}

// Container component that fetches data and passes to Shell (used in the app)
function Shell({
  children,
  notifications,
  requestHeaders,
  currentHubId,
  hasRtmdxUsers,
}: PropsWithChildren & {
  notifications: NotificationListItem[];
  requestHeaders: { [key: string]: string };
  currentHubId: string;
  hasRtmdxUsers: boolean;
}) {
  const rootData = useRouteLoaderData("root") as unknown as {
    user: User & { username: string; profile: string };
    hubs: Hub[];
    isHubAdmin: boolean;
    canEditCourses: boolean;
    canManageCrimeData: boolean;
    initialHubName?: string;
  };
  const { user, hubs, isHubAdmin, canEditCourses, canManageCrimeData, initialHubName } =
    rootData || {};

  const logoutFetcher = useFetcher({ key: "logout" });
  const fetcher = useFetcher();

  const handleLogout = () => {
    logoutFetcher.submit(null, {
      action: "/auth/logout",
      method: "post",
    });
  };

  const handleUpdateUserHub = (hubId: string, userEmail: string) => {
    fetcher.submit(
      { userEmail },
      {
        method: "post",
        action: getRouteWithCurrentHubId({
          currentHubId: hubId,
          route: "user/update",
        }),
      },
    );
  };

  return (
    <ShellPresentation
      notifications={notifications}
      requestHeaders={requestHeaders}
      currentHubId={currentHubId}
      hasRtmdxUsers={hasRtmdxUsers}
      user={user}
      hubs={hubs}
      isHubAdmin={isHubAdmin}
      canEditCourses={canEditCourses}
      canManageCrimeData={canManageCrimeData || false}
      onLogout={handleLogout}
      onUpdateUserHub={handleUpdateUserHub}
      initialHubName={initialHubName}
    >
      {children}
    </ShellPresentation>
  );
}

export default Shell;
